import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import { useAuth } from '../context/AuthContext';
import SubscriptionPlans from '../components/SubscriptionPlans';
import SubscriptionManagement from '../components/SubscriptionManagement';

const Subscription = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [business, setBusiness] = useState(null);
  const { businessId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if user is logged in
    if (!currentUser) {
      navigate('/login');
      return;
    }

    // Use current user's ID as business ID if none is provided in URL
    const effectiveBusinessId = businessId || currentUser.uid;
    console.log('Using business ID:', effectiveBusinessId);

    // Fetch business data
    const fetchBusiness = async () => {
      try {
        const businessDoc = await getDoc(doc(db, 'businesses', effectiveBusinessId));

        if (!businessDoc.exists()) {
          setError('Business not found');
          setLoading(false);
          return;
        }

        const businessData = businessDoc.data();
        setBusiness(businessData);

        // If business has an active subscription, show the management tab by default
        if (businessData.subscription &&
            (businessData.subscription.status === 'active' ||
             businessData.subscription.status === 'trialing')) {
          setActiveTab(1);
        }
        // Check localStorage for payment status as a fallback
        else if (localStorage.getItem('paymentComplete') === 'true') {
          console.log('Payment complete found in localStorage, showing management tab');
          setActiveTab(1);
        }
      } catch (err) {
        console.error('Error fetching business:', err);
        setError('Failed to load business information');
      } finally {
        setLoading(false);
      }
    };

    fetchBusiness();

    // Check for query parameters
    const params = new URLSearchParams(location.search);
    if (params.get('subscription') === 'success') {
      // Show success message and management tab
      setActiveTab(1);
    }
  }, [businessId, currentUser, navigate, location.search]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Subscription
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Manage your subscription plan and billing information
        </Typography>
      </Box>

      {/* Show success message if redirected from successful subscription */}
      {location.search.includes('subscription=success') && (
        <Alert severity="success" sx={{ mb: 4 }}>
          Your subscription has been successfully activated! You can now manage your subscription below.
        </Alert>
      )}

      {/* Show canceled message if redirected from canceled subscription */}
      {location.search.includes('subscription=canceled') && (
        <Alert severity="info" sx={{ mb: 4 }}>
          Your subscription process was canceled. You can try again when you're ready.
        </Alert>
      )}

      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Subscription Plans" />
          <Tab label="Manage Subscription" />
        </Tabs>
      </Paper>

      {activeTab === 0 && (
        <SubscriptionPlans
          businessId={businessId || currentUser.uid}
          onSubscriptionComplete={() => setActiveTab(1)}
        />
      )}

      {activeTab === 1 && (
        <SubscriptionManagement businessId={businessId || currentUser.uid} />
      )}
    </Container>
  );
};

export default Subscription;
